import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Timed auctions.
 *
 * Two rules from the database shape everything here:
 *
 *  - reserve_price is not selectable. Never add it to a column list; the request
 *    will be refused outright. Whether it is met comes from lotPublicState().
 *  - bids are placed only through place_bid(). There is no insert grant on
 *    auction_bids, so writing one directly cannot work.
 */

export type AuctionStatus = 'draft' | 'scheduled' | 'live' | 'closed' | 'cancelled';
export type LotStatus = 'pending' | 'declined' | 'live' | 'sold' | 'unsold' | 'withdrawn';

export interface Auction {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  opens_at: string;
  closes_at: string;
  status: AuctionStatus;
  terms: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuctionLot {
  id: string;
  auction_id: string;
  seller_id: string;
  lot_number: number | null;
  title: string;
  description: string | null;
  category: string | null;
  breed: string | null;
  quantity: number | null;
  location_town: string | null;
  county: string | null;
  start_price: number;
  current_bid: number | null;
  bid_count: number;
  leading_bidder: string | null;
  closes_at: string;
  status: LotStatus;
  declined_reason: string | null;
  created_at: string;
  updated_at: string;
}

/** Everything a bidder is allowed to know. Note what is absent: the reserve. */
export interface LotPublicState {
  id: string;
  currentBid: number | null;
  bidCount: number;
  nextBid: number;
  closesAt: string;
  status: LotStatus;
  hasReserve: boolean;
  reserveMet: boolean;
  youAreLeading: boolean;
  yourMax: number | null;
}

export interface BidResult {
  ok: true;
  leading: boolean;
  amount: number;
  yourMax: number;
  nextBid: number;
  closesAt: string;
  extended: boolean;
  reserveMet: boolean;
}

export interface AuctionBid {
  id: string;
  lot_id: string;
  bidder_id: string;
  max_amount: number;
  amount: number;
  was_leading: boolean;
  created_at: string;
}

const AUCTION_COLUMNS =
  'id, title, description, category, opens_at, closes_at, status, terms, created_at, updated_at';

// reserve_price is deliberately not here — selecting it is refused by the grant.
const LOT_COLUMNS =
  'id, auction_id, seller_id, lot_number, title, description, category, breed, ' +
  'quantity, location_town, county, start_price, current_bid, bid_count, ' +
  'leading_bidder, closes_at, status, declined_reason, created_at, updated_at';

/**
 * Housekeeping: close lots whose time is up, open sales that are due.
 *
 * Rides along with reads rather than a cron, same as listing expiry. Failure is
 * swallowed — a bidder should never see an error because tidy-up did not land.
 */
export async function sweepAuctions(supabase: SupabaseClient): Promise<void> {
  try {
    await supabase.rpc('close_due_lots');
  } catch {
    /* tidy-up only */
  }
}

export async function fetchAuctions(
  supabase: SupabaseClient,
  opts: { status?: AuctionStatus | AuctionStatus[] } = {}
): Promise<Auction[]> {
  await sweepAuctions(supabase);
  let q = supabase.from('auctions').select(AUCTION_COLUMNS);
  if (Array.isArray(opts.status)) q = q.in('status', opts.status);
  else if (opts.status) q = q.eq('status', opts.status);
  const { data, error } = await q.order('opens_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Auction[];
}

export async function fetchAuction(
  supabase: SupabaseClient,
  id: string
): Promise<Auction | null> {
  const { data, error } = await supabase
    .from('auctions')
    .select(AUCTION_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as Auction) ?? null;
}

export async function fetchLots(
  supabase: SupabaseClient,
  opts: { auctionId?: string; sellerId?: string; status?: LotStatus | LotStatus[] } = {}
): Promise<AuctionLot[]> {
  let q = supabase.from('auction_lots').select(LOT_COLUMNS);
  if (opts.auctionId) q = q.eq('auction_id', opts.auctionId);
  if (opts.sellerId) q = q.eq('seller_id', opts.sellerId);
  if (Array.isArray(opts.status)) q = q.in('status', opts.status);
  else if (opts.status) q = q.eq('status', opts.status);
  const { data, error } = await q
    .order('lot_number', { ascending: true, nullsFirst: false })
    .order('closes_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AuctionLot[];
}

export async function fetchLot(
  supabase: SupabaseClient,
  id: string
): Promise<AuctionLot | null> {
  const { data, error } = await supabase
    .from('auction_lots')
    .select(LOT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as AuctionLot) ?? null;
}

export async function lotPublicState(
  supabase: SupabaseClient,
  lotId: string
): Promise<LotPublicState | null> {
  const { data, error } = await supabase.rpc('lot_public_state', { lot: lotId });
  if (error) throw error;
  return (data as LotPublicState) ?? null;
}

/**
 * Place a bid.
 *
 * With proxy bidding on, `amount` is the most they are willing to pay and the
 * result's `amount` is what it actually stands at — usually less.
 */
export async function placeBid(
  supabase: SupabaseClient,
  lotId: string,
  amount: number
): Promise<BidResult> {
  const { data, error } = await supabase.rpc('place_bid', { lot: lotId, max_bid: amount });
  if (error) throw new Error(cleanBidError(error.message));
  return data as BidResult;
}

/**
 * Postgres prefixes its own errors; a bidder should just see the sentence.
 * The messages from place_bid() are already written for them.
 */
function cleanBidError(message: string): string {
  return message.replace(/^(ERROR:\s*)?/i, '').trim();
}

export async function fetchLotBids(
  supabase: SupabaseClient,
  lotId: string
): Promise<AuctionBid[]> {
  const { data, error } = await supabase
    .from('auction_bids')
    .select('id, lot_id, bidder_id, max_amount, amount, was_leading, created_at')
    .eq('lot_id', lotId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as AuctionBid[];
}

/** The lots a member is bidding on, for their dashboard. */
export async function fetchMyBidLots(
  supabase: SupabaseClient,
  memberId: string
): Promise<{ lot: AuctionLot; yourMax: number; leading: boolean }[]> {
  const { data: bids, error } = await supabase
    .from('auction_bids')
    .select('lot_id, max_amount')
    .eq('bidder_id', memberId);
  if (error) throw error;

  const best = new Map<string, number>();
  for (const b of (bids ?? []) as { lot_id: string; max_amount: number }[]) {
    best.set(b.lot_id, Math.max(best.get(b.lot_id) ?? 0, Number(b.max_amount)));
  }
  if (best.size === 0) return [];

  const { data: lots, error: lotErr } = await supabase
    .from('auction_lots')
    .select(LOT_COLUMNS)
    .in('id', [...best.keys()]);
  if (lotErr) throw lotErr;

  return ((lots ?? []) as AuctionLot[])
    .map((lot) => ({
      lot,
      yourMax: best.get(lot.id) ?? 0,
      leading: lot.leading_bidder === memberId,
    }))
    .sort((a, b) => new Date(a.lot.closes_at).getTime() - new Date(b.lot.closes_at).getTime());
}

// ── admin ───────────────────────────────────────────────────────────────────
export async function createAuction(
  supabase: SupabaseClient,
  auction: Partial<Auction> & { title: string; opens_at: string; closes_at: string }
): Promise<Auction> {
  const { data, error } = await supabase
    .from('auctions')
    .insert(auction)
    .select(AUCTION_COLUMNS)
    .single();
  if (error) throw error;
  return data as Auction;
}

export async function saveAuction(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<Auction>
): Promise<void> {
  const { error } = await supabase.from('auctions').update(patch).eq('id', id);
  if (error) throw error;
}

export async function setLotStatus(
  supabase: SupabaseClient,
  lotId: string,
  status: LotStatus,
  reason?: string
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_lot_status', {
    lot: lotId,
    new_status: status,
    reason: reason ?? null,
  });
  if (error) throw error;
}

/** Enter a lot into a sale. It starts pending review, enforced by a trigger. */
export async function createLot(
  supabase: SupabaseClient,
  lot: {
    auction_id: string;
    seller_id: string;
    title: string;
    description?: string | null;
    category?: string | null;
    breed?: string | null;
    quantity?: number | null;
    location_town?: string | null;
    county?: string | null;
    start_price?: number;
    reserve_price?: number | null;
  }
): Promise<string> {
  const { data, error } = await supabase
    .from('auction_lots')
    .insert(lot)
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

// ── presentation ────────────────────────────────────────────────────────────
export function money(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return `£${Number(amount).toLocaleString('en-GB', {
    minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * "2 days 4 hrs" / "4 min 12 sec".
 *
 * Deliberately shows seconds under an hour: in the last few minutes, when the
 * extension rule is in play, seconds are the only thing a bidder cares about.
 */
export function timeLeft(closesAt: string, now: number = Date.now()): string {
  const ms = new Date(closesAt).getTime() - now;
  if (ms <= 0) return 'Closed';

  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (d > 0) return `${d} day${d === 1 ? '' : 's'} ${h} hr${h === 1 ? '' : 's'}`;
  if (h > 0) return `${h} hr${h === 1 ? '' : 's'} ${m} min`;
  if (m > 0) return `${m} min ${sec} sec`;
  return `${sec} sec`;
}

/** True in the window where a bid extends the close. Drives the warning. */
export function inExtensionWindow(
  closesAt: string,
  extensionMinutes = 5,
  now: number = Date.now()
): boolean {
  const ms = new Date(closesAt).getTime() - now;
  return ms > 0 && ms <= extensionMinutes * 60_000;
}

export const LOT_STATUS_LABEL: Record<LotStatus, string> = {
  pending: 'Awaiting review',
  declined: 'Declined',
  live: 'Open for bidding',
  sold: 'Sold',
  unsold: 'Unsold — reserve not met',
  withdrawn: 'Withdrawn',
};
